import React from "react";
import { Button, Spinner } from "reactstrap";

import { formatAmountWithCurrency } from "../modals/payment/utils/currency";
import { copyToClipboard } from "../Tables/Paymenthistoryutils";
import {
    formatDateTime,
    isExpired,
    getLinkStatus,
    getClickedChip,
    getPaymentChip,
    canToggleLinkStatus,
    canDeleteLink,
} from "../Tables/Paymentlinkutils";

/**
 * PaymentLinkDetailPanel – Ödeme Linkleri listesinde seçilen linkin sağdan açılan detay paneli.
 *
 * Veriyi listedeki satırdan alır; kendi başına istek atmaz. Aksiyonlar listedeki
 * mevcut fonksiyonlara delege edilir (yeni iş mantığı yok).
 *
 * @param {object|null} link        – Seçili link (PaymentLinkDTO satırı)
 * @param {Function} onClose        – Paneli kapat
 * @param {object} actions          – { onOpenLink, onShowQr, onShare, onToggleStatus, onRotateToken, onDeleteLink, onOpenFullDetail }
 * @param {string[]} roles          – Oturumdaki kullanıcının rolleri (silme yetkisi için)
 * @param {string} shareButtonId    – Paylaş popover'ının hedef id'si
 * @param {boolean} statusLoading   – Durum güncellemesi sürüyor mu
 * @param {React.ReactNode} children– Panelin içine gömülecek ek içerik (paylaşım popover'ı)
 */
const PaymentLinkDetailPanel = ({
    link,
    onClose,
    actions = {},
    roles,
    shareButtonId,
    statusLoading = false,
    children,
}) => {
    const [copiedField, setCopiedField] = React.useState(null);

    if (!link) return null;

    const { onOpenLink, onShowQr, onShare, onToggleStatus, onRotateToken, onDeleteLink, onOpenFullDetail } = actions;

    const status = getLinkStatus(link);
    const clickedChip = getClickedChip(link);
    const paymentChip = getPaymentChip(link);
    const expired = isExpired(link.expiryDate);
    const isFixed = link.linkType === "FIXED";

    // Tahsil edilen tutar: sabit linklerde backend sayacı, tek kullanımlık linklerde
    // yalnızca ödeme gerçekleştiyse linkin tutarı bilinir.
    const collected = isFixed
        ? (link.totalCollected ?? 0)
        : (link.userPaid ? link.fixedAmount ?? null : 0);

    const handleCopy = async (value, field) => {
        await copyToClipboard(value);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 1500);
    };

    const InfoRow = ({ icon, label, children: value }) => (
        <div className="pd-info-row">
            <span className="pd-info-label">
                <i className={`bx ${icon}`} /> {label}
            </span>
            <span className="pd-info-value">{value}</span>
        </div>
    );

    return (
        <div className="pd-panel">
            <div className="pd-panel-header">
                <h6 className="mb-0 fw-bold text-dark">Link Detayı</h6>
                <button className="btn btn-sm btn-link text-muted p-0" onClick={onClose} title="Kapat">
                    <i className="bx bx-x fs-4" />
                </button>
            </div>

            <div className="pd-panel-body">
                {/* Link kodu ve kopyalama butonu kaldırıldı: hemen altındaki adres
                    kutusu zaten tam URL'i ve kopyalamayı sunuyordu. */}
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className={`chip ${status.chipClass}`}>
                        <i className="bx bx-check-circle" /> {status.label}
                    </span>
                </div>

                <div className="pd-amount">
                    {link.fixedAmount != null
                        ? formatAmountWithCurrency(link.fixedAmount, link.currency)
                        : isFixed && (link.minAmount != null || link.maxAmount != null)
                            ? `${formatAmountWithCurrency(link.minAmount ?? 0, link.currency)} - ${formatAmountWithCurrency(link.maxAmount ?? 0, link.currency)}`
                            : "Serbest tutar"}
                </div>

                {link.paymentLinkUrl && (
                    <div className="pd-url-box">
                        <span title={link.paymentLinkUrl}>{link.paymentLinkUrl}</span>
                        <button
                            className="icon-btn"
                            onClick={() => handleCopy(link.paymentLinkUrl, "url")}
                            title="Kopyala"
                        >
                            <i className={`bx ${copiedField === "url" ? "bx-check text-success" : "bx-copy"}`} />
                        </button>
                        <button className="icon-btn" onClick={() => onOpenLink?.(link)} title="Yeni sekmede aç">
                            <i className="bx bx-link-external" />
                        </button>
                    </div>
                )}

                <div className="pd-info-list">
                    <InfoRow icon="bx-calendar" label="Oluşturulma">
                        {formatDateTime(link.createdAt)}
                    </InfoRow>

                    <InfoRow icon="bx-time-five" label="Son Geçerlilik">
                        {link.expiryDate
                            ? <span className={expired ? "text-danger" : ""}>{formatDateTime(link.expiryDate)}</span>
                            : <span className="text-muted">Süresiz</span>}
                    </InfoRow>

                    <InfoRow icon="bx-user" label="Oluşturan">
                        {link.creatorName || "-"}
                    </InfoRow>

                    <InfoRow icon="bx-buildings" label="Şirket">
                        <span className="text-truncate d-inline-block" style={{ maxWidth: 180 }} title={link.companyName || "-"}>
                            {link.companyName || "-"}
                        </span>
                    </InfoRow>

                    <InfoRow icon="bx-id-card" label="Cari / Bayi">
                        <span className="text-truncate d-inline-block" style={{ maxWidth: 180 }} title={link.accountName || "-"}>
                            {link.accountName || "-"}
                        </span>
                    </InfoRow>

                    {link.invoiceCompanyName && (
                        <InfoRow icon="bx-receipt" label="Fatura Şirketi">
                            <span className="text-truncate d-inline-block" style={{ maxWidth: 180 }} title={link.invoiceCompanyName}>
                                {link.invoiceCompanyName}
                            </span>
                        </InfoRow>
                    )}

                    {link.phoneNumber && (
                        <InfoRow icon="bx-phone" label="Telefon">
                            {link.phoneNumber}
                        </InfoRow>
                    )}

                    <InfoRow icon="bx-link-alt" label="Link Tipi">
                        <span className={`chip ${isFixed ? "chip-purple" : "chip-navy"}`}>
                            {isFixed ? "Sabit" : "Tek Kullanımlık"}
                        </span>
                    </InfoRow>
                </div>

                <div className="pd-summary">
                    <div className="pd-summary-title">Link Performansı</div>

                    <div className="pd-summary-row">
                        <span>Tıklanma Sayısı</span>
                        <span>{link.clickCount || 0}</span>
                    </div>

                    <div className="pd-summary-row">
                        <span>Tıklanma Durumu</span>
                        <span>
                            {/* Sayı bir üstteki satırda; burada yalnızca durum gösterilir */}
                            <span className={`chip ${clickedChip.chipClass}`}>
                                {link.userClicked ? "Tıklandı" : "Tıklanmadı"}
                            </span>
                        </span>
                    </div>

                    <div className="pd-summary-row">
                        <span>Ödeme Durumu</span>
                        <span><span className={`chip ${paymentChip.chipClass}`}>{paymentChip.label}</span></span>
                    </div>

                    {isFixed && link.maxUsageCount != null && (
                        <div className="pd-summary-row">
                            <span>Kullanım Limiti</span>
                            <span>{link.paymentCount || 0} / {link.maxUsageCount}</span>
                        </div>
                    )}

                    <div className="pd-summary-row pd-summary-total">
                        <span>Tahsil Edilen Tutar</span>
                        <span>{collected != null ? formatAmountWithCurrency(collected, link.currency) : "-"}</span>
                    </div>
                </div>

                <div className="pd-actions">
                    {/* Tam detay, aksiyon yığınının başında — ödeme detay paneliyle aynı yerleşim. */}
                    {onOpenFullDetail && (
                        <Button
                            className="btn-rounded w-100 pd-full-link"
                            onClick={() => onOpenFullDetail(link)}
                        >
                            <i className="bx bx-detail me-1" /> Tüm Detayları Gör
                        </Button>
                    )}

                    <Button
                        className="btn-navy btn-rounded w-100"
                        onClick={() => onOpenLink?.(link)}
                        disabled={!link.paymentLinkUrl}
                    >
                        <i className="bx bx-link-external me-1" /> Bağlantıyı Aç
                    </Button>

                    <Button
                        className="btn-navy btn-rounded w-100"
                        onClick={() => onShowQr?.(link)}
                        disabled={!link.paymentLinkUrl}
                    >
                        <i className="bx bx-qr me-1" /> QR Oluştur
                    </Button>

                    <Button
                        id={shareButtonId}
                        className="btn-navy btn-rounded w-100"
                        onClick={() => onShare?.(link)}
                    >
                        <i className="bx bx-share-alt me-1" /> Linki Paylaş
                    </Button>

                    {/* Durum değişimi backend'de yalnızca sabit linkler için izinli */}
                    {canToggleLinkStatus(link) && (
                        <>
                            <Button
                                className="btn-orange btn-rounded w-100"
                                onClick={() => onToggleStatus?.(link)}
                                disabled={statusLoading}
                            >
                                {statusLoading
                                    ? <Spinner size="sm" />
                                    : <i className={`bx ${link.status === "ACTIVE" ? "bx-pause-circle" : "bx-play-circle"} me-1`} />}
                                {link.status === "ACTIVE" ? " Duraklat" : " Yeniden Aç"}
                            </Button>

                            <Button
                                color="danger"
                                outline
                                className="btn-rounded w-100"
                                onClick={() => onRotateToken?.(link)}
                            >
                                <i className="bx bx-revision me-1" /> Link Adresini Yenile
                            </Button>
                        </>
                    )}

                    {/* Silme geri alınamaz; backend bu ucu yalnız
                        SUPER_ADMIN / COMPANY_ADMIN'e açıyor. */}
                    {canDeleteLink(link, roles) && (
                        <Button
                            color="danger"
                            className="btn-rounded w-100"
                            onClick={() => onDeleteLink?.(link)}
                        >
                            <i className="bx bx-trash me-1" /> Linki Sil
                        </Button>
                    )}
                </div>

                {children}
            </div>
        </div>
    );
};

export default PaymentLinkDetailPanel;
